import { gql, TypedDocumentNode } from '@common/hooks';
import {
  ShiftCandidatesQueryCgResponse,
  ShiftCandidatesQueryVariables,
  OpenShiftCandidatesQueryCgResponse,
  OpenShiftCandidatesQueryVariables,
} from '@common/graphql';

// Backend-shaped candidate row / bucket set for one shift's detail panel.
// `shiftCandidates` is nullable (an unknown or foreign shiftId resolves to
// null), so unwrap both the nullable resolver and the nullable field.
export type ScheduleShiftCandidatesType = NonNullable<
  NonNullable<
    ShiftCandidatesQueryCgResponse['scheduleReview']
  >['shiftCandidates']
>;

export type ScheduleShiftCandidateRowType =
  ScheduleShiftCandidatesType['homeEligible'][number];

// A SECOND query against the same `scheduleReview` resolver (cf.
// schedule-health-snapshot.ts): the grid is fetched once by the page, and this
// resolves only the lazy `shiftCandidates(shiftId:)` field when a shift is
// selected — Apollo merges it onto the shared cache entry. The verdicts are
// recomputed on read against the current scoring weights (point-in-time).
export const SHIFT_CANDIDATES_QUERY: TypedDocumentNode<
  ShiftCandidatesQueryCgResponse,
  ShiftCandidatesQueryVariables
> = gql`
  query ShiftCandidates(
    $balancerRunId: ID!
    $organizationUnitId: ID!
    $shiftId: ID!
  ) {
    scheduleReview(
      balancerRunId: $balancerRunId
      organizationUnitId: $organizationUnitId
    ) {
      shiftCandidates(shiftId: $shiftId) {
        homeEligible {
          workerId
          fitScore
          eligible
          homeUnit
          reasons {
            key
            label
            hard
          }
        }
        homeNotEligible {
          workerId
          fitScore
          eligible
          homeUnit
          reasons {
            key
            label
            hard
          }
        }
        crossEligible {
          workerId
          fitScore
          eligible
          homeUnit
          reasons {
            key
            label
            hard
          }
        }
      }
    }
  }
`;

// Sibling of SHIFT_CANDIDATES_QUERY for OPEN demand cells: synthesized open rows
// carry no backing shift uuid, so the cell is addressed by its grid descriptor
// (week/day/band/role) instead. Same `scheduleReview` resolver, same nullable
// candidate-bucket shape, recomputed on read — fired only when the selected row
// is an open spot.
export const OPEN_SHIFT_CANDIDATES_QUERY: TypedDocumentNode<
  OpenShiftCandidatesQueryCgResponse,
  OpenShiftCandidatesQueryVariables
> = gql`
  query OpenShiftCandidates(
    $balancerRunId: ID!
    $organizationUnitId: ID!
    $weekKey: String!
    $dayKey: String!
    $shiftTypeSection: String!
    $role: String!
  ) {
    scheduleReview(
      balancerRunId: $balancerRunId
      organizationUnitId: $organizationUnitId
    ) {
      openShiftCandidates(
        weekKey: $weekKey
        dayKey: $dayKey
        shiftTypeSection: $shiftTypeSection
        role: $role
      ) {
        homeEligible {
          workerId
          fitScore
          eligible
          homeUnit
          reasons {
            key
            label
            hard
          }
        }
        homeNotEligible {
          workerId
          fitScore
          eligible
          homeUnit
          reasons {
            key
            label
            hard
          }
        }
        crossEligible {
          workerId
          fitScore
          eligible
          homeUnit
          reasons {
            key
            label
            hard
          }
        }
      }
    }
  }
`;
